import React from 'react';
import { faGears } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/ai-native-processes.jpg
import aiNativeProcessesImg from '../../../assets/images/blogs/ai-native-processes.jpg';

const AiNativeProcesses = () => (
    <BlogPost
        title="AI Native Processes: Building Learning Systems"
        subtitle="The process is the knowledge in motion. The knowledge is the process made explicit."
        category="Architecture"
        categoryIcon={faGears}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={aiNativeProcessesImg}
        imageAlt="AI-native processes - learning systems that evolve through execution"
        ctaHeading="Ready to build learning processes?"
        ctaText="Book a discovery call and we'll show you how AI-native processes learn continuously through action."
        seoDescription="Why separating knowledge from execution fails and how AI-native processes collapse the distinction to build systems that learn through doing."
        seoKeywords="AI-native processes, learning systems, knowledge management, process automation, agentic AI, Nebulyx"
        slug="ai-native-processes"
    >
        <p>
            I've been talking for a while on how we build processes in organisations and
            why the traditional approach keeps failing us.
        </p>

        <h3>The Old Separation</h3>

        <p>
            Process management said: document the flow first, then execute against it.
        </p>

        <p>
            Knowledge management said: capture the knowledge first, then apply it.
        </p>

        <p>
            Both assumed you could separate the knowing from the doing - defining everything
            upfront then running it.
        </p>

        <p>
            But that separation was always artificial and why so many transformation
            programmes stall.
        </p>

        <p>
            Years of documentation with flowcharts that don't match reality on first contact.
            Knowledge bases nobody uses. By the time you've defined everything, the world
            has moved on.
        </p>

        <h3>AI Native Processes Start Differently</h3>

        <p>
            AI Native processes don't start with complete documentation or perfect knowledge
            bases. They start with goals and domain knowledge, then learn through execution.
        </p>

        <p>
            I've found 70-80% of processes being fundamentally the same across organisations.
            Procurement is procurement. Onboarding is onboarding. The variation is often
            just regional quirks &amp; org chart contortions, not meaningful difference.
        </p>

        <p>
            It's also amazing how many processes are part of the big LLM models training
            set. That's embedded knowledge we can draw on.
        </p>

        <p>
            When we design processes with business people, we don't ask them to document
            everything first. We use AI to generate flows based on domain knowledge,
            industry patterns, and outcomes. Business people respond yes or no, they shape
            and refine for their specific context, and the process emerges through
            collaboration not committees.
        </p>

        <p>
            <strong>What used to take months can happen in hours.</strong>
        </p>

        <h3>Agents as Learning Units</h3>

        <p>
            Agents are not just execution engines - they are learning units.
        </p>

        <p>
            Our agents operate within bounded decision spaces, grounded in domain knowledge.
            As they execute, they capture every decision with full context: state,
            constraints, reasoning, outcome.
        </p>

        <p>
            That's not just audit trail. That's the raw material for learning - this is
            where Context Graphs come in.
        </p>

        <p>
            New pattern emerges? The agent surfaces it. Edge case handled differently?
            Captured &amp; available as precedent. SME overrides the agent's recommendation?
            The judgment is recorded in the moment, not lost in someone's head.
        </p>

        <p>
            Traditional processes were statically defined by people long before implementation.
            Document once, execute forever, update occasionally when someone notices it's
            broken.
        </p>

        <p>
            <strong>But agents learn from their own execution - the process gets smarter
            as it runs.</strong>
        </p>

        <h3>The Collapse</h3>

        <p>
            Process management &amp; knowledge management were always artificially separated.
            Different teams, different tools, different methodologies.
        </p>

        <p>
            AI Native collapses them back together.
        </p>

        <blockquote className="blog-post-quote">
            "The process is the knowledge in motion. The knowledge is the process made
            explicit. Agents capture both through execution."
        </blockquote>

        <p>
            Build fast with AI. Learn continuously through action. Every agent is an
            emergent system that compounds knowledge as it runs.
        </p>

        <p>
            That's the shift. 🎯
        </p>
    </BlogPost>
);

export default AiNativeProcesses;
